import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { ASSETS, APPLY_URL } from "../lib/constants";

export default function Header() {
  const nav = [
    { label: "Institute", href: "#about" },
    { label: "Method", href: "#method" },
    { label: "Mentor", href: "#mentor" },
    { label: "Voices", href: "#testimonials" },
    { label: "Program", href: "#program" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.76, 0, 0.24, 1] }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-cream/75 border-b border-ink-15"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 py-4 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
          <img
            src={ASSETS.logo}
            alt="NavSankalpIAS"
            className="h-9 md:h-10 w-auto object-contain"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-8">
          {nav.map((n) => (
            <a
              key={n.label}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-[12px] tracking-[0.22em] uppercase text-ink/70 hover:text-saffron transition-colors duration-300 font-medium"
            >
              {n.label}
            </a>
          ))}
        </nav>

        {/* Animated top-right CTA — Register for free updates (Program 1 link) */}
        <motion.a
          href={APPLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="header-free-updates-btn"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.6, duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          className="relative inline-flex items-center gap-2 md:gap-2.5 bg-saffron text-cream hover:text-cream px-4 md:px-5 py-2.5 md:py-3 rounded-full font-semibold text-[11px] md:text-xs tracking-[0.14em] uppercase overflow-hidden shrink-0"
          style={{
            boxShadow:
              "0 10px 30px -8px rgba(217,105,28,0.55), 0 0 0 0 rgba(217,105,28,0.6)",
          }}
          aria-label="Register for free for all future updates"
        >
          {/* Pulse ring */}
          <span
            aria-hidden
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              boxShadow: "0 0 0 0 rgba(217,105,28,0.55)",
              animation: "cta-pulse 2.2s cubic-bezier(0.4,0,0.6,1) infinite",
            }}
          />
          {/* Shine sweep */}
          <span
            aria-hidden
            className="absolute inset-0 pointer-events-none overflow-hidden rounded-full"
          >
            <span
              className="absolute top-0 h-full w-1/3"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                animation: "cta-shine 3.2s ease-in-out infinite",
              }}
            />
          </span>

          <motion.span
            aria-hidden
            animate={{ rotate: [0, 15, -10, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="relative z-10 inline-flex"
          >
            <Sparkles size={14} strokeWidth={2} />
          </motion.span>
          <span className="relative z-10 hidden md:inline">
            Register for Free · All Future Updates
          </span>
          <span className="relative z-10 md:hidden">Free Updates</span>
          <span className="relative z-10 hidden md:inline">→</span>
        </motion.a>
      </div>

      {/* CTA keyframes — inline so we don't need to touch global CSS */}
      <style>{`
        @keyframes cta-pulse {
          0% { box-shadow: 0 0 0 0 rgba(217,105,28,0.55); }
          70% { box-shadow: 0 0 0 14px rgba(217,105,28,0); }
          100% { box-shadow: 0 0 0 0 rgba(217,105,28,0); }
        }
        @keyframes cta-shine {
          0% { transform: translateX(-150%) skewX(-20deg); }
          60% { transform: translateX(220%) skewX(-20deg); }
          100% { transform: translateX(220%) skewX(-20deg); }
        }
      `}</style>
    </motion.header>
  );
}
